enum Breakpoint {
  TEN = 10, // Smallest
  TWENTY = 20,
  THIRTY = 30,
  FORTY = 40,
  FIFTY = 50,
  SIXTY = 60,
  SEVENTY = 70,
  EIGHTY = 80,
  NINETY = 90, // Largest
}

export type TBreakpointOption = Breakpoint | TBreakpointKeys;
type TBreakpointKeys = keyof typeof Breakpoint;

// Change here if any breakpoint size changes
const TEN_BREAKPOINT = 500;
const TWENTY_BREAKPOINT = 751;
const THIRTY_BREAKPOINT = 964;
const FORTY_BREAKPOINT = 1131;
const FIFTY_BREAKPOINT = 1394;
const SIXTY_BREAKPOINT = 1571;
const SEVENTY_BREAKPOINT = 1758;
const EIGHTY_BREAKPOINT = 1871;
const NINETY_BREAKPOINT = 2560;

/*
    Access using:
    BREAKPOINTS[Breakpoint.TEN] / BREAKPOINTS[10]  / BREAKPOINTS.TEN / BREAKPOINTS["TEN"]
    or
    BP[Bp.TEN] / BP[10] / BP.TEN / BP["TEN"]
*/
const BREAKPOINTS: Record<Breakpoint | TBreakpointKeys, number> = {
  [Breakpoint.EIGHTY]: EIGHTY_BREAKPOINT,
  [Breakpoint.FIFTY]: FIFTY_BREAKPOINT,
  [Breakpoint.FORTY]: FORTY_BREAKPOINT,
  [Breakpoint.NINETY]: NINETY_BREAKPOINT,
  [Breakpoint.SEVENTY]: SEVENTY_BREAKPOINT,
  [Breakpoint.SIXTY]: SIXTY_BREAKPOINT,
  [Breakpoint.TEN]: TEN_BREAKPOINT,
  [Breakpoint.THIRTY]: THIRTY_BREAKPOINT,
  [Breakpoint.TWENTY]: TWENTY_BREAKPOINT,
  EIGHTY: EIGHTY_BREAKPOINT,
  FIFTY: FIFTY_BREAKPOINT,
  FORTY: FORTY_BREAKPOINT,
  NINETY: NINETY_BREAKPOINT,
  SEVENTY: SEVENTY_BREAKPOINT,
  SIXTY: SIXTY_BREAKPOINT,
  TEN: TEN_BREAKPOINT,
  THIRTY: THIRTY_BREAKPOINT,
  TWENTY: TWENTY_BREAKPOINT,
};

const BREAKPOINTS_MEDIA_QUERIES: Record<Breakpoint | TBreakpointKeys, string> = {
  [Breakpoint.EIGHTY]: `@media (min-width: ${EIGHTY_BREAKPOINT}px)`,
  [Breakpoint.FIFTY]: `@media (min-width: ${FIFTY_BREAKPOINT}px)`,
  [Breakpoint.FORTY]: `@media (min-width: ${FORTY_BREAKPOINT}px)`,
  [Breakpoint.NINETY]: `@media (min-width: ${NINETY_BREAKPOINT}px)`,
  [Breakpoint.SEVENTY]: `@media (min-width: ${SEVENTY_BREAKPOINT}px)`,
  [Breakpoint.SIXTY]: `@media (min-width: ${SIXTY_BREAKPOINT}px)`,
  [Breakpoint.TEN]: `@media (min-width: ${TEN_BREAKPOINT}px)`,
  [Breakpoint.THIRTY]: `@media (min-width: ${THIRTY_BREAKPOINT}px)`,
  [Breakpoint.TWENTY]: `@media (min-width: ${TWENTY_BREAKPOINT}px)`,
  EIGHTY: `@media (min-width: ${EIGHTY_BREAKPOINT}px)`,
  FIFTY: `@media (min-width: ${FIFTY_BREAKPOINT}px)`,
  FORTY: `@media (min-width: ${FORTY_BREAKPOINT}px)`,
  NINETY: `@media (min-width: ${NINETY_BREAKPOINT}px)`,
  SEVENTY: `@media (min-width: ${SEVENTY_BREAKPOINT}px)`,
  SIXTY: `@media (min-width: ${SIXTY_BREAKPOINT}px)`,
  TEN: `@media (min-width: ${TEN_BREAKPOINT}px)`,
  THIRTY: `@media (min-width: ${THIRTY_BREAKPOINT}px)`,
  TWENTY: `@media (min-width: ${TWENTY_BREAKPOINT}px)`,
};

/* Usage:
  If you plan to use container queries you must specify a parent container by assigning
    containerName: CONTAINER_NAME
    containerType: "inline-size"
  to the parent element's style. Then you can use the BP_CONTAINER_QUERIES
  to target child elements based on the parent's width.
*/
export const CONTAINER_NAME = "parentContainer";
const BREAKPOINTS_CONTAINER_QUERIES: Record<Breakpoint | TBreakpointKeys, string> = {
  [Breakpoint.EIGHTY]: `@container ${CONTAINER_NAME} (min-width: ${EIGHTY_BREAKPOINT}px)`,
  [Breakpoint.FIFTY]: `@container ${CONTAINER_NAME} (min-width: ${FIFTY_BREAKPOINT}px)`,
  [Breakpoint.FORTY]: `@container ${CONTAINER_NAME} (min-width: ${FORTY_BREAKPOINT}px)`,
  [Breakpoint.NINETY]: `@container ${CONTAINER_NAME} (min-width: ${NINETY_BREAKPOINT}px)`,
  [Breakpoint.SEVENTY]: `@container ${CONTAINER_NAME} (min-width: ${SEVENTY_BREAKPOINT}px)`,
  [Breakpoint.SIXTY]: `@container ${CONTAINER_NAME} (min-width: ${SIXTY_BREAKPOINT}px)`,
  [Breakpoint.TEN]: `@container ${CONTAINER_NAME} (min-width: ${TEN_BREAKPOINT}px)`,
  [Breakpoint.THIRTY]: `@container ${CONTAINER_NAME} (min-width: ${THIRTY_BREAKPOINT}px)`,
  [Breakpoint.TWENTY]: `@container ${CONTAINER_NAME} (min-width: ${TWENTY_BREAKPOINT}px)`,
  EIGHTY: `@container ${CONTAINER_NAME} (min-width: ${EIGHTY_BREAKPOINT}px)`,
  FIFTY: `@container ${CONTAINER_NAME} (min-width: ${FIFTY_BREAKPOINT}px)`,
  FORTY: `@container ${CONTAINER_NAME} (min-width: ${FORTY_BREAKPOINT}px)`,
  NINETY: `@container ${CONTAINER_NAME} (min-width: ${NINETY_BREAKPOINT}px)`,
  SEVENTY: `@container ${CONTAINER_NAME} (min-width: ${SEVENTY_BREAKPOINT}px)`,
  SIXTY: `@container ${CONTAINER_NAME} (min-width: ${SIXTY_BREAKPOINT}px)`,
  TEN: `@container ${CONTAINER_NAME} (min-width: ${TEN_BREAKPOINT}px)`,
  THIRTY: `@container ${CONTAINER_NAME} (min-width: ${THIRTY_BREAKPOINT}px)`,
  TWENTY: `@container ${CONTAINER_NAME} (min-width: ${TWENTY_BREAKPOINT}px)`,
};

export {
  Breakpoint as Bp,
  BREAKPOINTS as BP,
  CONTAINER_NAME as BP_CONTAINER_NAME,
  BREAKPOINTS_CONTAINER_QUERIES as BP_CONTAINER_QUERIES,
  BREAKPOINTS_MEDIA_QUERIES as BP_MEDIA_QUERIES,
  Breakpoint,
  BREAKPOINTS,
};
